import { Box, Container, Divider, SimpleGrid } from '@hover/blueprint';
import { Button } from '@hover/blueprint/brand';
import { navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import React, { useEffect, useRef, useState } from 'react';
import { DownloadAppModal } from '../../common';
import { Progress } from '../../ui';
import ArrowButton from '../../ui/ArrowButton';
import CustomAccordion from './CustomAccordion';
import ProductionHeader from './ProductionHeader';
import ExteriorContractorProductionVector from '../../../assets/svgs/ExteriorContractorProductionVector';
import HoverMaterials from '../../../assets/videos/Hover_Materials.mp4';

interface Props {
  realTimeListItems: string[];
  orderDirectlyListItems: string[];
}

const ProductionDesktop: React.FC<Props> = ({
  realTimeListItems,
  orderDirectlyListItems,
}) => {
  const [isActive, setIsActive] = useState<boolean>(true);
  const [mounted, setMounted] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const unsub = setInterval(() => {
      setIsActive(prev => !prev);
    }, 6400);
    setMounted(true);
    return () => clearInterval(unsub);
  }, []);

  useEffect(() => {
    if (videoRef.current && !isActive) {
      void videoRef.current.play();
    }
  }, [isActive]);

  const navigateTo = () => navigate('/pricing');
  const toggleModal = () => setIsOpenModal(!isOpenModal);

  return (
    <SimpleGrid
      spacing={600}
      columns={2}
      d={{ base: 'none', desktop: 'flex' }}
      alignItems="center"
      h="600px"
    >
      <DownloadAppModal isOpen={isOpenModal} onClose={toggleModal} />
      <Container flexDir="column" maxW="580px" px={0} mr="auto">
        <ProductionHeader mb="56px" />
        <Box maxH="400px" overflow="hidden" flexDir="column">
          {isActive && mounted ? (
            <Progress duration={6.5} />
          ) : (
            <Divider borderWidth="1px" color="neutral.300" my={0} opacity={1} />
          )}
          <CustomAccordion
            isActive={isActive}
            header="Direct Ordering"
            listItems={realTimeListItems}
            body="Supplier direct ordering from the HOVER app makes everything simpler."
          />
          {!isActive && mounted ? (
            <Progress duration={6.5} />
          ) : (
            <Divider
              my={0}
              borderWidth="1px"
              color="neutral.300"
              mt={isActive ? '26px' : 0}
            />
          )}
          <CustomAccordion
            isActive={!isActive}
            header="Labor Management"
            listItems={orderDirectlyListItems}
            body="With just a few clicks, meet every project's specific installation needs"
          />
          <Divider
            my={0}
            borderWidth="1px"
            color="neutral.300"
            mt={!isActive ? '26px' : 0}
          />
        </Box>
        <Box flexDir={{ base: 'column', tablet: 'row' }} mt={800}>
          <Button
            colorScheme="primary"
            variant="solid"
            mb={{ base: 500, desktop: 0 }}
            w={{ base: '100%', tablet: 'auto' }}
            onClick={() => toggleModal()}
          >
            Start measuring in minutes
          </Button>
          <ArrowButton
            variant="minimal"
            w={{ base: '100%', tablet: 'auto' }}
            mb={{ base: 600, desktop: 0 }}
            // FIXME(links): this should be a `GatsbyLink`
            onClick={() => navigateTo()}
          >
            Go to pricing
          </ArrowButton>
        </Box>
      </Container>
      <Container d="flex" justifyContent="flex-end">
        <Box
          maxW="377px"
          maxH="537px"
          position="relative"
          d={isActive ? 'flex' : 'none'}
          mr={1100}
        >
          <Box position="absolute" top="-100px" right="-100px">
            <ExteriorContractorProductionVector />
          </Box>
          <StaticImage
            quality={100}
            placeholder="blurred"
            objectFit="contain"
            alt="real time 3d"
            src="../../../assets/images/exterior_contractor_simple_production_solution.png"
          />
        </Box>
        <Box
          maxH="328px"
          maxW="580px"
          position="relative"
          d={!isActive ? 'block' : 'none'}
          bg="red"
        >
          <video ref={videoRef} src={HoverMaterials} muted playsInline />
        </Box>
      </Container>
    </SimpleGrid>
  );
};

export default ProductionDesktop;
